import type { FC } from 'react'

import { useEffect } from 'react'

/*
 * Drives every .reveal element on the page. Elements already on screen when the page loads
 * are shown straight away; anything further down is hidden after load and fades in as it
 * scrolls into view. The server never hides anything, so the page reads fine without JS.
 */
export const RevealObserver: FC = (): null => {
    useEffect(() => {
        const elements = Array.from(document.querySelectorAll<Element>('.reveal:not(.reveal-shown)'))
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        const show = (element: Element) => {
            element.classList.remove('reveal-hidden')
            element.classList.add('reveal-shown')
        }

        if (reduceMotion || typeof IntersectionObserver === 'undefined') {
            elements.forEach(show)

            return
        }

        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (!entry.isIntersecting) return

                show(entry.target)
                observer.unobserve(entry.target)
            })
        }, { rootMargin: '0px 0px -10% 0px', threshold: 0.08 })

        elements.forEach(element => {
            const rect = element.getBoundingClientRect()

            if (rect.top < window.innerHeight * 0.9) {
                show(element)

                return
            }

            element.classList.add('reveal-hidden')
            observer.observe(element)
        })

        return () => observer.disconnect()
    }, [])

    return null
}
